"use client";

import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, AlertCircle, Info, X } from "lucide-react";

interface ToastProps {
  message: string;
  onClose: () => void;
  type?: "success" | "error" | "info";
  duration?: number;
}

export function Toast({ message, onClose, type = "error", duration = 4000 }: ToastProps) {
  const [isLeaving, setIsLeaving] = useState(false);

  const handleDismiss = () => {
    setIsLeaving(true);
    setTimeout(() => {
      onClose();
    }, 450); // 450ms smooth exit transition before unmounting
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      handleDismiss();
    }, duration);
    return () => clearTimeout(timer);
  }, [duration]);

  // Industry-standard professional color schemes
  const config =
    type === "success"
      ? {
          bg: "bg-[#ECFDF5] border-[#A7F3D0] text-[#065F46] dark:bg-[#064E3B]/95 dark:border-[#047857] dark:text-[#ECFDF5]",
          icon: <CheckCircle2 className="h-5 w-5 text-[#10B981] shrink-0" />,
        }
      : type === "info"
      ? {
          bg: "bg-[#FFF3E0] border-[#FFE0B2] text-[#9A3412] dark:bg-[#7C2D12]/95 dark:border-[#9A3412] dark:text-[#FFF3E0]",
          icon: <Info className="h-5 w-5 text-[#FF9500] shrink-0" />,
        }
      : {
          bg: "bg-[#FEF2F2] border-[#FCA5A5] text-[#991B1B] dark:bg-[#7F1D1D]/95 dark:border-[#991B1B] dark:text-[#FEF2F2]",
          icon: <AlertCircle className="h-5 w-5 text-[#EF4444] shrink-0" />,
        };

  const el = (
    <div
      role="alert"
      className={`fixed bottom-5 left-4 right-4 sm:left-auto sm:right-6 sm:max-w-md z-[10000] rounded-2xl border ${config.bg} p-4 shadow-2xl backdrop-blur-md transition-all duration-500 ease-in-out ${
        isLeaving
          ? "opacity-0 translate-y-4 scale-95 pointer-events-none"
          : "opacity-100 translate-y-0 scale-100"
      } flex items-start justify-between gap-3 text-xs sm:text-sm font-semibold`}
    >
      <div className="flex items-start gap-3 min-w-0">
        {config.icon}
        <p className="leading-snug pt-0.5">{message}</p>
      </div>
      <button
        onClick={handleDismiss}
        className="p-1 rounded-lg opacity-70 hover:opacity-100 hover:bg-black/5 dark:hover:bg-white/10 transition-opacity shrink-0"
        title="Dismiss notification"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(el, document.body) : null;
}
